import { DAY_MS } from '@/utils/dates'

// The first summary ratings were stored on this day, so "Any time" starts here on both tabs.
export const ALL_TIME_START = '2026-09-25T00:00:00.000Z'

export const startOfUtcDay = (ms: number) => ms - (((ms % DAY_MS) + DAY_MS) % DAY_MS)

export type DatePreset = '1d' | '7d' | '30d'

const PRESET_DAYS: Record<DatePreset, number> = { '1d': 1, '7d': 7, '30d': 30 }

/** Start of a preset: UTC midnight that many days back, counting today as the first day. */
export const presetStart = (preset: DatePreset) =>
  new Date(startOfUtcDay(Date.now()) - (PRESET_DAYS[preset] - 1) * DAY_MS).toISOString()

/** The preset a window matches exactly, if any; an older bookmark or a custom range matches none. */
export const matchingPreset = (since: string | undefined, until: string | undefined): DatePreset | undefined =>
  !since || until ? undefined : (Object.keys(PRESET_DAYS) as DatePreset[]).find((p) => presetStart(p) === since)

// The stats endpoint rejects longer windows, even with weekly buckets.
export const MAX_RATES_SPAN_MS = 3 * 366 * DAY_MS

export const readTimestamp = (value: string | null | undefined): string | undefined => {
  const ms = value ? Date.parse(value) : Number.NaN
  return Number.isFinite(ms) ? new Date(ms).toISOString() : undefined
}

/**
 * The closed window the rates read: "Any time" means everything stored, through the end of today. An open end is
 * widened to keep the window non-empty, and the start is clamped to the longest span the stats endpoint accepts.
 */
export const ratesWindow = (since: string | undefined, until: string | undefined) => {
  const endOfToday = startOfUtcDay(Date.now()) + DAY_MS
  const sinceMs = since ? Date.parse(since) : undefined
  const untilMs = until ? Date.parse(until) : Math.max(endOfToday, startOfUtcDay(sinceMs ?? 0) + DAY_MS)
  let start = sinceMs ?? Date.parse(ALL_TIME_START)
  if (start >= untilMs) start = untilMs - DAY_MS
  start = Math.max(start, untilMs - MAX_RATES_SPAN_MS)
  return { since: new Date(start).toISOString(), until: new Date(untilMs).toISOString() }
}

export const dayOfSince = (since: string | undefined) => since?.slice(0, 10) ?? ''
export const dayOfUntil = (until: string | undefined) =>
  until ? new Date(Date.parse(until) - 1).toISOString().slice(0, 10) : ''
